import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import * as THREE from 'three';
import { ViewportGizmo } from 'three-viewport-gizmo';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { CadObject } from '../../models/cad-object.model';
import { DFMCheck } from '../../models/dfm-check.model';
import { PullDirection } from '../../models/pull-direction.model';
import { MouldSolution } from '../../models/mould-solution.model';
import { Badge } from 'primeng/badge';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { applyNewTopoFaceColors, buildMeshes } from './helpers/build-mesh';
import { createAxes } from './helpers/create-axes';
import { createGrid } from './helpers/create-grid';
import {
  clearDFMVisualization,
  createDFMVisualizationState,
  DFMVisualizationState,
  displayDFMCheck,
} from './helpers/display-dfm-check';
import { MeasureTool } from './helpers/handle-measure-tool';
import {
  clearHoverHighlight,
  createHoverHighlightState,
  handleHoverHighlight,
  HoverHighlightState,
  setHoverHighlight,
} from './helpers/hover-highlight';
import { initializeCamera } from './helpers/initialize-camera';
import { initializeControls } from './helpers/initialize-controls';
import { initializeLighting } from './helpers/initialize-lighting';
import { initializeRenderer } from './helpers/initialize-renderer';
import { initializeViewportGizmo } from './helpers/initialize-viewport-gizmo';
import { isWebGLAvailable } from './helpers/is-webgl-available';
import { renderCadObjects } from './helpers/render-cad-objects';
import { renderPullDirectionArrows } from './helpers/render-pull-direction-arrows';
import { resetView } from './helpers/reset-view';
import { resizeRenderer } from './helpers/resize-renderer';
import { startAnimationLoop } from './helpers/start-animation-loop';
import { toggleFaceEdges as toggleFaceEdgesFn } from './helpers/toggle-face-edges';
import { toggleOpacity as toggleOpacityFn } from './helpers/toggle-opacity';
import {
  applyDraftAnalysis,
  clearDraftAnalysis,
  DraftAnalysisResult,
} from './helpers/draft-analysis';
import {
  clearPullDirectionHoverPreview,
  createPullDirectionPickingState,
  pickPullDirectionFromClick,
  updatePullDirectionHoverPreview,
} from './helpers/pull-direction-picking';
import {
  clearMouldSolution,
  displayMouldSolution,
} from './helpers/display-mould-solution';

const INITIAL_ANIMATION_ID = 0;
const DEFAULT_BACKGROUND_COLOR = new THREE.Color('GhostWhite');

@Component({
  selector: 'app-cad-viewer',
  imports: [Button, Tooltip, Badge],
  templateUrl: './cad-viewer.component.html',
  styleUrl: './cad-viewer.component.scss',
  host: {
    '(window:resize)': 'onResize()',
  },
})
export class CadViewerComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('moldCanvas') canvas!: ElementRef;

  @Input({ required: true }) cadObjects: CadObject[] = [];
  @Input() pullDirections: PullDirection[] = [];
  @Input() selectedPullDirection: PullDirection | null = null;
  @Input() dfmCheck: DFMCheck | null = null; // If set, the appropriate DFM check result will be visualized in the viewer.
  @Input() visibleCadObjectIds: Set<number> | null = null; // If set, only these CAD objects will be rendered. This is used when linking CAD objects to quote items so the user can focus on the relevant geometry.
  @Input() highlightCadObject: CadObject | null = null;
  @Input() externallyHoveredCadObjectId: number | null = null;
  @Input() showDraftAnalysis = false;
  @Input() maxDraftAngle = 2.0;
  @Input() enablePullDirectionPicking = false;
  @Input() mouldSolution: MouldSolution | null = null; // If set, each piece's faces are highlighted in its own colour.

  @Output() draftAnalysisResult = new EventEmitter<DraftAnalysisResult>();
  @Output() pullDirectionPicked = new EventEmitter<PullDirection>();
  // Emits only for hovers originating on the canvas, not for externallyHoveredCadObjectId.
  @Output() hoveredCadObjectIdChange = new EventEmitter<number | null>();

  protected get hasPlyBlob(): boolean {
    return this.cadObjects.some((obj) => obj.plyBlob != null);
  }

  private renderer!: THREE.WebGLRenderer;
  private camera!: THREE.PerspectiveCamera;
  private controls!: OrbitControls;
  private directionalLight!: THREE.DirectionalLight;

  private gridHelper: THREE.GridHelper | null = null;
  private axesHelper: THREE.AxesHelper | null = null;
  private viewportGizmo!: ViewportGizmo;
  private hoverState: HoverHighlightState = createHoverHighlightState();
  private pullDirectionPickingState = createPullDirectionPickingState();
  private lastCanvasHoveredCadObjectId: number | null = null;

  // CAD meshes live here so operations like raycasting and opacity only target model geometry.
  protected objectGroup = new THREE.Group();
  // Expose helper so the template can call it directly without a wrapper method.
  protected readonly toggleOpacity = toggleOpacityFn;
  protected readonly toggleFaceEdges = toggleFaceEdgesFn;
  private dfmState: DFMVisualizationState = createDFMVisualizationState();
  // Pull-direction overlays stay separate so they can be managed without affecting CAD mesh state.
  private arrowGroup = new THREE.Group();

  private scene = new THREE.Scene().add(this.objectGroup).add(this.arrowGroup);
  private animationId: number = INITIAL_ANIMATION_ID;
  private initialized = false;
  private meshesReady = false;
  private resizeObserver?: ResizeObserver;

  readonly measureTool = new MeasureTool();

  // The div wrapping the canvas — used as the Three.js render target and gizmo container.
  private get canvasContainer(): HTMLElement {
    return this.canvas.nativeElement.parentElement;
  }

  ngAfterViewInit(): void {
    if (!isWebGLAvailable()) return;

    // Dialog overlays can be laid out after this view initializes, so observe
    // the container and initialize or resize the viewer once its dimensions
    // become available.
    this.resizeObserver = new ResizeObserver(() => this.onResize());
    this.resizeObserver.observe(this.canvasContainer);
    this.initializeViewer();
  }

  private initializeViewer(): void {
    if (this.initialized) return;

    const { clientWidth, clientHeight } = this.canvasContainer;
    if (clientWidth <= 0 || clientHeight <= 0) return;

    this.renderer = initializeRenderer(
      this.canvas.nativeElement,
      this.canvasContainer,
    );
    this.camera = initializeCamera(this.canvasContainer, this.scene);
    this.controls = initializeControls(this.camera, this.canvas.nativeElement);
    this.directionalLight = initializeLighting(this.scene);
    this.viewportGizmo = initializeViewportGizmo(
      this.camera,
      this.renderer,
      this.controls,
      this.canvasContainer,
    );
    this.scene.background = DEFAULT_BACKGROUND_COLOR;
    this.measureTool.addToScene(this.scene);
    // LineMaterial requires manual resolution updates on resize.
    this.measureTool.updateResolution(
      this.canvasContainer.clientWidth,
      this.canvasContainer.clientHeight,
    );

    this.initialized = true;
    this.refreshViewerGeometry();
    startAnimationLoop(
      this.camera,
      this.directionalLight,
      this.renderer,
      this.scene,
      (animationId) => {
        this.animationId = animationId;
      },
      () => this.viewportGizmo.render(),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.initialized) return;

    if (changes['cadObjects']) {
      clearHoverHighlight(this.hoverState);
      if (this.measureTool.active) {
        this.measureTool.deactivate();
      }
      this.refreshViewerGeometry();
    } else if (changes['visibleCadObjectIds']) {
      renderCadObjects(this.objectGroup, this.getVisibleCadObjects());
    }

    if (changes['pullDirections'] || changes['selectedPullDirection']) {
      renderPullDirectionArrows(
        this.arrowGroup,
        this.objectGroup,
        this.pullDirections,
        this.selectedPullDirection,
      );
    }

    if (changes['dfmCheck']) {
      clearDFMVisualization(this.cadObjects, this.scene, this.dfmState);
      if (this.dfmCheck) {
        displayDFMCheck(
          this.dfmCheck,
          this.cadObjects,
          this.scene,
          this.dfmState,
        );
      }
    }

    if (changes['highlightCadObject'] && !changes['cadObjects']) {
      // Only the overlay changes; reuse the existing meshes and camera.
      if (this.meshesReady) {
        for (const cadObject of this.cadObjects) {
          applyNewTopoFaceColors(cadObject, this.highlightCadObject);
        }
      }
    }

    if (changes['mouldSolution'] && this.meshesReady) {
      displayMouldSolution(this.cadObjects, this.mouldSolution);
    }

    if (changes['externallyHoveredCadObjectId']) {
      this.updateExternalHoverHighlight();
    }

    if (
      changes['showDraftAnalysis'] ||
      changes['maxDraftAngle'] ||
      changes['selectedPullDirection']
    ) {
      this.updateDraftAnalysis();
      this.updateExternalHoverHighlight();
    }
  }

  doResetView(): void {
    const bbox = resetView(this.objectGroup, this.camera, this.controls);
    if (bbox) {
      this.gridHelper = createGrid(this.scene, bbox, this.gridHelper);
      this.axesHelper = createAxes(this.scene, bbox, this.axesHelper);
    }
  }

  onCanvasMouseDown(event: MouseEvent): void {
    if (this.measureTool.active && this.camera) {
      this.measureTool.handleClick(
        event,
        this.canvas.nativeElement,
        this.camera,
        this.objectGroup,
      );
    }
  }

  onCanvasMouseMove(event: MouseEvent): void {
    if (!this.camera || !this.canvas) return;

    if (this.enablePullDirectionPicking && !this.measureTool.active) {
      updatePullDirectionHoverPreview(
        event,
        this.canvas.nativeElement as HTMLCanvasElement,
        this.camera,
        this.objectGroup,
        this.scene,
        this.pullDirectionPickingState,
      );
    } else {
      clearPullDirectionHoverPreview(
        this.scene,
        this.pullDirectionPickingState,
      );
    }

    // Don't change material colours while draft analysis colouring is active.
    if (this.showDraftAnalysis) {
      clearHoverHighlight(this.hoverState);
      this.emitCanvasHover(null);
      return;
    }

    handleHoverHighlight(
      event,
      this.canvas.nativeElement as HTMLCanvasElement,
      this.camera,
      this.objectGroup,
      this.cadObjects,
      this.hoverState,
    );
    this.emitCanvasHover(this.hoverState.currentHoveredCadObject?.id ?? null);
  }

  onCanvasMouseLeave(): void {
    clearHoverHighlight(this.hoverState);
    this.emitCanvasHover(null);
    this.updateExternalHoverHighlight();
    clearPullDirectionHoverPreview(this.scene, this.pullDirectionPickingState);
  }

  onCanvasClick(event: MouseEvent): void {
    if (!this.enablePullDirectionPicking || !this.camera || !this.canvas) {
      return;
    }
    if (this.measureTool.active) return;

    const pickedDirection = pickPullDirectionFromClick(
      event,
      this.canvas.nativeElement as HTMLCanvasElement,
      this.camera,
      this.objectGroup,
      this.pullDirectionPickingState,
    );
    if (!pickedDirection) return;

    this.pullDirectionPicked.emit(pickedDirection);
  }

  toggleGrid(): void {
    if (this.gridHelper) this.gridHelper.visible = !this.gridHelper.visible;
  }

  toggleAxes(): void {
    if (this.axesHelper) this.axesHelper.visible = !this.axesHelper.visible;
  }

  protected onResize(): void {
    if (!this.initialized) {
      this.initializeViewer();
      return;
    }
    if (
      this.canvasContainer.clientWidth <= 0 ||
      this.canvasContainer.clientHeight <= 0
    ) {
      return;
    }
    resizeRenderer(this.renderer, this.camera, this.canvasContainer);
    this.measureTool.updateResolution(
      this.canvasContainer.clientWidth,
      this.canvasContainer.clientHeight,
    );
    this.viewportGizmo?.update();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    clearHoverHighlight(this.hoverState);
    clearPullDirectionHoverPreview(this.scene, this.pullDirectionPickingState);
    clearDFMVisualization(this.cadObjects, this.scene, this.dfmState);
    clearMouldSolution(this.cadObjects);
    cancelAnimationFrame(this.animationId);
    this.renderer?.dispose();
  }

  private getVisibleCadObjects(): CadObject[] {
    if (!this.visibleCadObjectIds) return this.cadObjects;
    const ids = this.visibleCadObjectIds;
    return this.cadObjects.filter((obj) => ids.has(obj.id));
  }

  /** Returns the camera's current world direction (pointing into the scene). */
  getCameraDirection(): THREE.Vector3 {
    const dir = new THREE.Vector3();
    if (this.camera) {
      this.camera.getWorldDirection(dir);
    }
    return dir;
  }

  private updateDraftAnalysis(): void {
    if (!this.initialized) return;
    if (!this.meshesReady) return;

    this.objectGroup.updateMatrixWorld(true);

    if (this.selectedPullDirection) {
      const result = applyDraftAnalysis(
        this.cadObjects,
        this.selectedPullDirection,
        this.maxDraftAngle,
      );
      this.draftAnalysisResult.emit(result);
      if (!this.showDraftAnalysis) {
        clearDraftAnalysis(this.cadObjects);
      }
    } else {
      clearDraftAnalysis(this.cadObjects);
      this.draftAnalysisResult.emit({
        draftArea: 0,
        visibleArea: 0,
        hiddenArea: 0,
      });
    }
  }

  private refreshViewerGeometry(): void {
    this.meshesReady = false;
    void buildMeshes(this.cadObjects, this.highlightCadObject).then(() => {
      if (!this.initialized) return;
      this.meshesReady = true;

      renderCadObjects(this.objectGroup, this.getVisibleCadObjects());
      // Fresh meshes have no edge overlays; toggle once so face edges start on.
      toggleFaceEdgesFn(this.objectGroup);
      renderPullDirectionArrows(
        this.arrowGroup,
        this.objectGroup,
        this.pullDirections,
        this.selectedPullDirection,
      );

      if (this.dfmCheck) {
        clearDFMVisualization(this.cadObjects, this.scene, this.dfmState);
        displayDFMCheck(
          this.dfmCheck,
          this.cadObjects,
          this.scene,
          this.dfmState,
        );
      }

      displayMouldSolution(this.cadObjects, this.mouldSolution);
      this.updateDraftAnalysis();
      this.updateExternalHoverHighlight();
      this.doResetView();
    });
  }

  private emitCanvasHover(cadObjectId: number | null): void {
    if (cadObjectId === this.lastCanvasHoveredCadObjectId) return;
    this.lastCanvasHoveredCadObjectId = cadObjectId;
    this.hoveredCadObjectIdChange.emit(cadObjectId);
  }

  private updateExternalHoverHighlight(): void {
    if (!this.initialized || this.showDraftAnalysis) {
      clearHoverHighlight(this.hoverState);
      return;
    }
    const cadObject =
      this.cadObjects.find(
        (object) => object.id === this.externallyHoveredCadObjectId,
      ) ?? null;
    setHoverHighlight(cadObject, this.hoverState);
  }
}
